/**
 * Block-level AST Nodes
 *
 * Blocks represent structural units:
 * - TextBlock: The question/instruction text
 * - LineBlock: A visual row containing inline elements
 */

import { z } from 'zod';
import { InlineNodeSchema, type InlineNode } from './inline';
import { AST_TYPES } from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
// Text Block (Question)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Text block schema - represents the question/instruction
 * 
 * @example
 * {
 *   type: "TextBlock",
 *   value: "أقارن كل عددين باستعمال الرمر المناسب"
 * }
 */
export const TextBlockSchema = z.object({
  type: z.literal(AST_TYPES.TEXT_BLOCK),
  value: z.string()
});

export type TextBlock = z.infer<typeof TextBlockSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Line Block (Visual Row)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Line block schema - represents a visual row of inline elements
 * 
 * One line = one visual row in the UI
 * Contains an ordered sequence of inline elements
 * 
 * @example
 * {
 *   type: "LineBlock",
 *   inlines: [
 *     { type: "HighlightedText", value: "151 000", highlight: [0, 3] },
 *     { type: "InlineInput", name: "x", length: 1 },
 *     { type: "HighlightedText", value: "54 000", highlight: [0, 3] }
 *   ]
 * }
 */
export const LineBlockSchema = z.object({
  type: z.literal(AST_TYPES.LINE_BLOCK),
  inlines: z.array(InlineNodeSchema)
});

export type LineBlock = z.infer<typeof LineBlockSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Factory Functions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a text block (question)
 */
export function createTextBlock(value: string): TextBlock {
  return TextBlockSchema.parse({
    type: AST_TYPES.TEXT_BLOCK,
    value
  });
}

/**
 * Create a line block
 */
export function createLineBlock(inlines: InlineNode[]): LineBlock {
  return LineBlockSchema.parse({
    type: AST_TYPES.LINE_BLOCK,
    inlines
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Type Guards
// ═══════════════════════════════════════════════════════════════════════════

export function isTextBlock(value: unknown): value is TextBlock {
  return TextBlockSchema.safeParse(value).success;
}

export function isLineBlock(value: unknown): value is LineBlock {
  return LineBlockSchema.safeParse(value).success;
}
